import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase-config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FiBook, FiClock, FiUser, FiEdit, FiTrash2, FiMoreVertical } from 'react-icons/fi';
import './theme.css';
import FlashcardsNav from './FlashcardsNav';

const FlashcardsHome = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingDeck, setDeletingDeck] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDecks, setFilteredDecks] = useState([]);
  const [notification, setNotification] = useState(null);

  // Filter decks based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredDecks(decks);
    } else {
      const filtered = decks.filter(deck => 
        deck.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredDecks(filtered);
    }
  }, [decks, searchQuery]);

  // Show notification and auto-hide after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  useEffect(() => {
    const fetchUserDecks = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Get user's personal flashcard collection
        const userDecksRef = collection(db, 'users', user.uid, 'flashcards');
        const userDecksSnapshot = await getDocs(userDecksRef);
        
        const userDeckIds = [];
        userDecksSnapshot.forEach((doc) => {
          userDeckIds.push(doc.id);
        });

        // Fetch the actual deck data from the library collection
        const decksData = [];
        for (const deckId of userDeckIds) {
          try {
            const deckDoc = await getDoc(doc(db, 'flashcardLibrary', deckId));
            if (deckDoc.exists()) {
              const data = deckDoc.data();
              const parsedData = JSON.parse(data.deck);
              decksData.push({
                id: deckId,
                title: parsedData.title || 'Untitled Deck',
                subject: parsedData.subject || 'Islamic Studies',
                gradeLevel: parsedData.gradeLevel || '',
                languageMode: parsedData.languageMode || 'mixed',
                cards: parsedData.cards?.length || 0,
                createdAt: data.createdAt,
                lastEdited: data.lastEdited,
                ownerID: data.ownerID,
                tags: data.tags || [],
                public: data.public || false,
                isPersonal: true,
                isOwner: data.ownerID === user.uid
              });
            }
          } catch (parseError) {
            console.error('Error parsing deck data:', parseError);
          }
        }

        // Sort by creation date (newest first)
        decksData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDecks(decksData);
      } catch (err) {
        console.error('Error fetching user decks:', err);
        setError('Failed to load your flashcards');
      } finally {
        setLoading(false);
      }
    };

    fetchUserDecks();
  }, [user]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.deck-menu-button')) {
        setShowMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const deleteDeck = async (deckId) => {
    if (!user) return;
    
    if (!window.confirm('Are you sure you want to delete this deck? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingDeck(deckId);
      
      // Delete from user's personal collection
      const userDeckRef = doc(db, 'users', user.uid, 'flashcards', deckId);
      await deleteDoc(userDeckRef);
      
      // If user is the owner, also delete from library
      const deck = decks.find(d => d.id === deckId);
      if (deck && deck.isOwner) {
        const libraryDeckRef = doc(db, 'flashcardLibrary', deckId);
        await deleteDoc(libraryDeckRef);
      }
      
      // Remove from local state
      setDecks(decks.filter(d => d.id !== deckId));
      setShowMenu(null);
      
      showNotification('Deck deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting deck:', error);
      showNotification('Failed to delete deck. Please try again.', 'error');
    } finally {
      setDeletingDeck(null);
    }
  };

  const editDeck = (deckId) => {
    // Navigate to edit page (we'll implement this later)
    navigate(`/tools/flashcards/edit/${deckId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays < 7) return `${diffDays - 1} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getCategoryColor = (subject) => {
    const colors = {
      'Islamic Studies': 'var(--gradient-primary)',
      'Quran': 'linear-gradient(135deg, #059669, #10b981)',
      'Hadith': 'linear-gradient(135deg, #7c3aed, #8b5cf6)',
      'Fiqh': 'linear-gradient(135deg, #dc2626, #ef4444)',
      'Arabic': 'linear-gradient(135deg, #ea580c, #f97316)',
      'History': 'linear-gradient(135deg, #0891b2, #06b6d4)',
      'Aqeedah': 'linear-gradient(135deg, #059669, #10b981)',
      'Dua': 'linear-gradient(135deg, #7c3aed, #8b5cf6)'
    };
    return colors[subject] || 'var(--gradient-primary)';
  };

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
        <main className="container" style={{ paddingTop: 32, paddingBottom: 32 }}>
          <FlashcardsNav />
          <div style={{ textAlign: 'center', padding: 48 }}>
            <div className="text-foreground" style={{ fontSize: 18, marginBottom: 16 }}>
              Please sign in to view your flashcards
            </div>
            <button className="button primary" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'hsl(var(--background))' }}>
      {/* Notification Component */}
      {notification && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '12px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: '500',
            zIndex: 1000,
            background: notification.type === 'success' ? '#10b981' : '#ef4444',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'slideIn 0.3s ease-out'
          }}
        >
          {notification.message}
        </div>
      )}

      <main className="container" style={{ paddingTop: 24, paddingBottom: 48 }}>
        <FlashcardsNav />

        <section className="center" style={{ paddingTop: 24, paddingBottom: 24 }}>
          <h1 style={{ fontSize: 40, fontWeight: 800, marginBottom: 12 }}>
            <span className="text-primary">Experience</span> Interactive Learning
          </h1>
          <p className="text-muted-foreground" style={{ maxWidth: 720, margin: '0 auto 16px' }}>
            Explore your personal collection of flashcards, complete with Arabic text, transliterations, and detailed explanations.
          </p>
          <button className="button primary" onClick={() => navigate('/tools/flashcards/create')}>
            Create Flashcards
          </button>
        </section>

        <section style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <h2 className="text-foreground" style={{ fontSize: 24, fontWeight: 700 }}>Your Flashcards</h2>
            <div className="text-muted-foreground" style={{ fontSize: 14 }}>
              {loading ? 'Loading...' : `${filteredDecks.length} deck${filteredDecks.length !== 1 ? 's' : ''}`}
            </div>
          </div>

          {/* Search Bar */}
          <div style={{ marginBottom: 24 }}>
            <input
              type="text"
              placeholder="Search your flashcards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border rounded"
              style={{ 
                width: '100%', 
                padding: '12px 16px', 
                fontSize: 16,
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                background: 'white'
              }}
            />
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div className="text-muted-foreground">Loading your flashcards...</div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div className="error-message">{error}</div>
            </div>
          ) : filteredDecks.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <div className="rounded-xl border" style={{ padding: 32, background: 'var(--gradient-subtle)', maxWidth: 400, margin: '0 auto' }}>
                <div className="text-primary" style={{ fontSize: 48, marginBottom: 16 }}>
                  <FiBook />
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
                  {searchQuery ? 'No matching flashcards' : 'No Flashcards Yet'}
                </h3>
                <p className="text-muted-foreground" style={{ marginBottom: 16 }}>
                  {searchQuery 
                    ? `No flashcards found matching "${searchQuery}". Try a different search term.`
                    : 'Create your first flashcard deck or save some from the library to start learning'
                  }
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  {searchQuery ? (
                    <button 
                      className="button outline" 
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </button>
                  ) : (
                    <>
                      <button className="button primary" onClick={() => navigate('/tools/flashcards/create')}>
                        Create Your First Deck
                      </button>
                      <button className="button outline" onClick={() => navigate('/tools/flashcards/library')}>
                        Browse Library
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-3">
              {filteredDecks.map((deck) => (
                <div 
                  key={deck.id} 
                  className="card hover:shadow-elegant" 
                  style={{ 
                    cursor: 'pointer', 
                    transition: 'all 0.2s ease',
                    padding: 20
                  }}
                  onClick={() => navigate(`/flashcards/${deck.id}`)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                      <h3 className="text-foreground" style={{ fontSize: 18, fontWeight: 600, marginBottom: 4, lineHeight: 1.3 }}>
                        {deck.title}
                      </h3>
                      <p className="text-muted-foreground" style={{ fontSize: 14, marginBottom: 8 }}>
                        {deck.subject}
                        {deck.gradeLevel && ` • ${deck.gradeLevel}`}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div 
                        className="rounded-lg" 
                        style={{ 
                          width: 48, 
                          height: 48, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: getCategoryColor(deck.subject),
                          color: 'white',
                          fontSize: 20,
                          flexShrink: 0
                        }}
                      >
                        <FiBook />
                      </div>
                      
                      {/* Deck Management Menu */}
                      {deck.isOwner && (
                        <div style={{ position: 'relative' }}>
                          <button
                            className="deck-menu-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowMenu(showMenu === deck.id ? null : deck.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              color: 'hsl(var(--muted-foreground))'
                            }}
                          >
                            <FiMoreVertical size={16} />
                          </button>
                          
                          {showMenu === deck.id && (
                            <div 
                              className="deck-menu"
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                background: 'white',
                                border: '1px solid hsl(var(--border))',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 1000,
                                minWidth: '120px'
                              }}
                            >
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editDeck(deck.id);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  background: 'none',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontSize: '14px'
                                }}
                              >
                                <FiEdit size={14} />
                                Edit
                              </button>
                              <button
                                className="menu-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteDeck(deck.id);
                                }}
                                disabled={deletingDeck === deck.id}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  background: 'none',
                                  border: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  fontSize: '14px',
                                  color: 'hsl(var(--destructive))',
                                  opacity: deletingDeck === deck.id ? 0.6 : 1
                                }}
                              >
                                <FiTrash2 size={14} />
                                {deletingDeck === deck.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiBook style={{ fontSize: 14 }} />
                        <span className="text-muted-foreground" style={{ fontSize: 14 }}>
                          {deck.cards} card{deck.cards !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FiClock style={{ fontSize: 14 }} />
                        <span className="text-muted-foreground" style={{ fontSize: 14 }}>
                          {formatDate(deck.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="badge" style={{ fontSize: 12 }}>
                      {deck.languageMode}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button 
                      className="button primary" 
                      style={{ flex: 1, fontSize: 14 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tools/flashcards/study/${deck.id}`);
                      }}
                    >
                      Study
                    </button>
                    <button 
                      className="button outline" 
                      style={{ fontSize: 14 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/flashcards/${deck.id}`);
                      }}
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {filteredDecks.length > 0 && (
          <section style={{ marginTop: 48, textAlign: 'center' }}>
            <div className="rounded-xl border" style={{ padding: 24, background: 'var(--gradient-subtle)' }}>
              <div className="text-primary" style={{ fontSize: 48, marginBottom: 12 }}>
                <FiBook />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>Need More Content?</h3>
              <p className="text-muted-foreground" style={{ marginBottom: 12 }}>
                Explore our library for more Islamic educational content
              </p>
              <button 
                className="button outline" 
                onClick={() => navigate('/tools/flashcards/library')}
                style={{ marginRight: 8 }}
              >
                Browse Library
              </button>
              <button 
                className="button primary" 
                onClick={() => navigate('/tools/flashcards/create')}
              >
                Create New Deck
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default FlashcardsHome; 